import React, { useState } from 'react';
import { resetPasswordWithToken } from '../services/api';

// PR-FP: the "set a new password" screen. Reached by clicking the link in
// the password-reset email, which looks like:
//
//     https://<frontend>/reset-password?token=<64-hex-chars>
//
// The app has no React Router -- routing is state-based in App.js -- so
// App.js detects the /reset-password path on mount and renders this
// component directly. The token is read from the query string here.
//
// Password rules mirror the backend (min 8 chars, enforced by zod on
// POST /auth/reset-password). The confirm field is a client-side
// convenience only; the backend never sees it.
function ResetPassword({ onDone }) {
    // Read ?token= once, at module render. URLSearchParams handles the
    // parsing; a missing token yields null and we show an error state.
    const token = new URLSearchParams(window.location.search).get('token');

    const [newPassword,     setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error,           setError]           = useState('');
    const [loading,         setLoading]         = useState(false);
    const [done,            setDone]            = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            await resetPasswordWithToken(token, newPassword);
            setDone(true);
        } catch (err) {
            // The backend returns a generic "invalid or expired" 400 for
            // any bad-token case, plus 400s for validation failures.
            setError(err.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // No token in the URL at all -- the link was malformed or hand-typed.
    const missingToken = !token;

    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">Choose a new password</p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    {missingToken ? (
                        <div className="space-y-6">
                            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
                                This reset link is missing its token. Request a
                                new link from the sign-in page.
                            </div>
                            <button
                                onClick={onDone}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                Back to sign in
                            </button>
                        </div>
                    ) : done ? (
                        <div className="space-y-6">
                            <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                                Your password has been updated. You can now sign
                                in with your new password.
                            </div>
                            <button
                                onClick={onDone}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                Go to sign in
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label
                                        htmlFor="reset-new-password"
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                    >
                                        New password
                                    </label>
                                    <input
                                        id="reset-new-password"
                                        type="password"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="At least 8 characters"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="reset-confirm-password"
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                    >
                                        Confirm new password
                                    </label>
                                    <input
                                        id="reset-confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="••••••••"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    {loading ? 'Updating...' : 'Update password'}
                                </button>
                            </form>
                            <div className="text-center mt-6 pt-6 border-t border-gray-800">
                                <button
                                    onClick={onDone}
                                    className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                                >
                                    Back to sign in
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;